import { TransformFn, POJO } from "objectro/lib/types";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { get } from "../../utils/helpers";
import { ISO2_TO_ISO3, ISO3_TO_ISO2 } from "../../utils/countries";

const SOURCE_BY_ZONE = {
  USA: "99330",
  JPN: "99332",
  AUS: "100004",
  AUT: "100014",
  BEL: "100019",
  CAN: "100008",
  CHL: "100026",
  CHN: "100012",
  CZE: "100006",
  DNK: "100028",
  FRA: "100013",
  DEU: "100007",
  HKG: "100010",
  IRL: "100018",
  ITA: "100023",
  SAU: "100029",
  KWT: "100031",
  MAC: "100011",
  MYS: "100017",
  MEX: "100021",
  NLD: "100020",
  NZL: "100027",
  PHL: "100032",
  PRI: "100034",
  SGP: "100005",
  KOR: "100016",
  ESP: "100024",
  SWE: "100030",
  CHE: "100015",
  TWN: "100009",
  THA: "100025",
  ARE: "100022",
  GBR: "100003",
  VNM: "100033"
};

const transformPostalAddressToInternalAddress: TransformFn = (value: any) => {
  return {
    streetAddress1: get(value, "streetAddress1", null),
    streetAddress2: get(value, "streetAddress2", null),
    streetAddress3: get(value, "streetAddress3", null),
    addressLocality: get(value, "city", null),
    addressRegion: get(value, "region", null),
    postalCode: get(value, "postalCode", null),
    addressCountry: get(value, "country")
  };
};

const getTransformPhoneFn = (name: string) => {
  const transformPhoneFn = (
    value: string | null | undefined,
    propName: string,
    input: POJO
  ) => {
    if (value) {
      const addressCountryIso2: string =
        input.addressCountry && ISO3_TO_ISO2[input.addressCountry];
      const parsedPhone = parsePhoneNumberFromString(value);
      if (parsedPhone) {
        let countryIso2 = parsedPhone.country;
        if (!countryIso2) {
          const allCodes = get(parsedPhone, "metadata.country_calling_codes");
          const codes =
            allCodes && allCodes[parsedPhone.countryCallingCode + ""];
          if (codes) {
            countryIso2 =
              addressCountryIso2 && codes.indexOf(addressCountryIso2) >= 0
                ? addressCountryIso2
                : codes[0];
          }
        }
        if (
          parsedPhone.country === "JP" &&
          parsedPhone.nationalNumber[0] !== "0"
        ) {
          parsedPhone.nationalNumber = "0" + parsedPhone.nationalNumber;
        }
        return {
          [name]: parsedPhone.nationalNumber,
          [name + "Country"]: countryIso2 && ISO2_TO_ISO3[countryIso2]
        };
      }
    }
  };
  return transformPhoneFn;
};

const getDateTransformFn = (name: string) => {
  const transformDateFn: TransformFn = (
    value: Date | number | string | null | undefined
  ) => {
    return {
      [name]: value ? new Date(value).toISOString() : null
    };
  };
  return transformDateFn;
};

// Map the DDC Backend Customer object to the customerDataState
// DDCCustomer -> customerFormState

export const fromDDCCustomer = {
  identifier: (value: string, propName: string, input: POJO) => {
    return {
      identifier: value,
      barcode: value
    };
  },
  gender: "gender",
  title: "honorificPrefix",
  firstName: "givenName",
  lastName: "familyName",
  alternateFirstName: "alternateGivenName1",
  alternateLastName: "alternateFamilyName1",

  birthDate: "birthDate",
  calendarType: "calendarType",
  email: "email",
  phone: "telephone",
  phoneType: "phoneType",
  //mobilePhoneNumber: "mobileTelephone",
  // This won't be mapped to a nested object within the customer data object
  // The internal data model requires a shallow object
  address: transformPostalAddressToInternalAddress,
  companyName: "companyName",
  appsConsent: "appsConsent",
  emailConsent: "emailConsent",
  postConsent: "postConsent",
  phoneConsent: "phoneConsent",
  advisorCode: "salesPerson",
  // special tiffany fields
  middleName: "middleName",
  maritalStatus: "maritalStatus",
  profession: "profession",
  weddingDate: "weddingDate",
  anniversary: "anniversary",
  spouseBirthday: "spouseBirthday",
  nationality: "nationality",

  confirmConsent: "confirmConsent",

  salesforceCustomerId: "salesforceCustomerId",
  sfAddressId: "sfAddressId",
  sfAnniversaryId: "sfAnniversaryId",
  sfBirthdayId: "sfBirthdayId",
  sfEmailId: "sfEmailId",
  sfPhoneId: "sfPhoneId",
  //sfHomePhoneId: "sfHomePhoneId",
  //sfMobilePhoneId: "sfMobilePhoneId",
  sfSpouseBirthdayId: "sfSpouseBirthdayId",
  sfWeddingId: "sfWeddingId",
  sfAlternateNameId: "sfAlternateNameId",
  sfProfessionId: "sfProfessionId"
};

export const fromDDCCustomerJapan = {
  ...fromDDCCustomer,
  identifier: (value: string, propName: string, input: POJO) => {
    if (value.length < 12) {
      value = value.padStart(12, "0");
    }
    return {
      identifier: value,
      barcode: value
    };
  }
};

// Map the internal data model to the DDC Backend Customer object
// customerFormState -> DDCCustomer
export const toDDCCustomer = {
  identifier: "ecmId",
  honorificPrefix: "salutation",
  givenName: "firstName",
  familyName: "lastName",
  gender: "gender",
  alternateGivenName1: "alternateFirstName",
  alternateFamilyName1: "alternateLastName",
  // In case the date is not a string, we convert to a YYYY-MM-DD string
  birthDate: getDateTransformFn("birthDay"),
  calendarType: "calendarType",
  email: "emailAddress",

  addressCountry: "country",
  streetAddress1: "address1",
  streetAddress2: "address2",
  streetAddress3: "address3",
  addressLocality: "city",
  addressRegion: "state",
  postalCode: "postCode",
  companyName: "companyName",

  //mobileTelephone: getTransformPhoneFn("mobilePhone"),
  telephone: getTransformPhoneFn("phone"),
  phoneType: "phoneType",
  profession: "profession",
  maritalStatus: "relationshipStatus",
  weddingDate: getDateTransformFn("dateOfWedding"),
  anniversary: getDateTransformFn("anniversary"),
  spouseBirthday: getDateTransformFn("spouseBirthday"),
  nationality: "nationality",
  confirmConsent: (
    value: string | boolean | null | undefined,
    propName: string,
    input: POJO
  ) => {
    return {
      consent:
        input.storeLocation === "jpn" && (!value || value === "false")
          ? false
          : true
    };
  },
  signatureData: "signatureData",

  creationStore: "store",
  storeLocation: (value: string) => {
    const zone = value && value.toUpperCase();
    return {
      storeLocation: zone,
      sourceId: get(SOURCE_BY_ZONE, zone)
    };
  },
  preferredLanguage: "language",
  salesforceCustomerId: "salesforceCustomerId",
  sfAddressId: "sfAddressId",
  sfEmailId: "sfEmailId",
  sfPhoneId: "sfPhoneId",
  //sfMobilePhoneId: "sfMobilePhoneId",
  //sfHomePhoneId: "sfHomePhoneId",
  sfBirthdayId: "sfBirthdayId",
  sfAnniversaryId: "sfAnniversaryId",
  sfWeddingId: "sfWeddingId",
  sfSpouseBirthdayId: "sfSpouseBirthdayId",
  sfAlternateNameId: "sfAlternateNameId",
  sfProfessionId: "sfProfessionId",

  // clear unnecessary fields
  address: null,
  advisorCode: null,
  appsConsent: null,
  salesPerson: null,
  birthDateDay: null,
  birthDateMonth: null,
  birthDateYear: null,
  creationDate: null,
  customFields: null,
  emailConsent: null,
  phoneConsent: null,
  postConsent: null,
  storeCountry: null,
  storeName: null,
  eReceipt: null
};

export const processApiCustomerResponse = {
  response: "message",
  status: "status",
  message: "message",
  ecmId: (value: string) => {
    return {
      id: value,
      barcode: value
    };
  }
};
export const processApiCustomerResponseJapan = {
  response: "message",
  status: "status",
  message: "message",
  ecmId: (value: string) => {
    if (value && value.length < 12) {
      value = value.padStart(12, "0");
    }
    return {
      id: value,
      barcode: value
    };
  }
};
